import {
  faCheck,
  faCirclePlay,
  faCircleStop,
  faHourglassHalf,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import DateTile from 'src/components/ui/DateTile';
import { useDateLocale } from 'src/hooks/useDateLocale';
import { summarizeWindowDuration } from 'src/utils/maintenance';

/** One labelled row in the downtime "when" list: icon + label, then a value. */
const DowntimeInfoRow = ({ icon, label, value }) => (
  <div className='mnt-dtwhen__row'>
    <span className='mnt-dtwhen__key'>
      <FontAwesomeIcon icon={icon} /> {label}
    </span>
    <span className='mnt-dtwhen__value'>{value}</span>
  </div>
);

DowntimeInfoRow.propTypes = {
  icon: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

DowntimeInfoRow.defaultProps = { value: null };

/**
 * Full bordered downtime card — used when *viewing* the window linked to an
 * event (principle: full to view). `chip` and `actions` are rendered slots.
 */
export const DowntimeViewCard = ({ downtime, chip, actions, finishOverride }) => {
  const { formattedDateTime } = useDateLocale();
  const fmtFull = value => formattedDateTime(value) || '—';
  const duration = finishOverride ? '' : summarizeWindowDuration(downtime.start, downtime.finish);
  return (
    <div className='mnt-dtcard'>
      <div className='mnt-dtcard__top'>
        {downtime.start && <DateTile date={downtime.start} color='amber' />}
        <div className='mnt-dtcard__title'>{downtime.description || 'Downtime window'}</div>
        {chip}
      </div>
      <div className='mnt-dtwhen'>
        <DowntimeInfoRow icon={faCirclePlay} label='Start' value={fmtFull(downtime.start)} />
        <DowntimeInfoRow
          icon={faCircleStop}
          label='Finish'
          value={finishOverride || fmtFull(downtime.finish)}
        />
        {duration && <DowntimeInfoRow icon={faHourglassHalf} label='Duration' value={duration} />}
      </div>
      {actions && <div className='mnt-dtcard__actions'>{actions}</div>}
    </div>
  );
};

DowntimeViewCard.propTypes = {
  downtime: PropTypes.object.isRequired,
  chip: PropTypes.node,
  actions: PropTypes.node,
  finishOverride: PropTypes.string,
};

DowntimeViewCard.defaultProps = { chip: null, actions: null, finishOverride: null };

export const DowntimePickRow = ({ downtime, selected, onSelect, readOnly }) => {
  const { formattedDateTime } = useDateLocale();

  const body = (
    <>
      {downtime.start && <DateTile date={downtime.start} color='amber' size='sm' />}
      <div className='mnt-dtpick__main'>
        <div className='mnt-dtpick__title'>{downtime.description || 'Downtime window'}</div>
        <div className='mnt-dtpick__rows'>
          <div className='mnt-dtpick__row'>
            <span className='mnt-dtpick__k'>
              <FontAwesomeIcon icon={faCirclePlay} /> Start
            </span>
            <span className='mnt-dtpick__v'>{formattedDateTime(downtime.start) || '—'}</span>
          </div>
          <div className='mnt-dtpick__row'>
            <span className='mnt-dtpick__k'>
              <FontAwesomeIcon icon={faCircleStop} /> Finish
            </span>
            <span className='mnt-dtpick__v'>{formattedDateTime(downtime.finish) || '—'}</span>
          </div>
        </div>
      </div>
      {!readOnly && (
        <span className='mnt-dtpick__check'>{selected && <FontAwesomeIcon icon={faCheck} />}</span>
      )}
    </>
  );

  if (readOnly) return <div className='mnt-dtpick mnt-dtpick--static'>{body}</div>;

  return (
    <div
      className={classNames('mnt-dtpick', { 'mnt-dtpick--on': selected })}
      role='button'
      tabIndex={0}
      onClick={() => onSelect(downtime)}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') onSelect(downtime);
      }}
    >
      {body}
    </div>
  );
};

DowntimePickRow.propTypes = {
  downtime: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  onSelect: PropTypes.func,
  readOnly: PropTypes.bool,
};

DowntimePickRow.defaultProps = { selected: false, onSelect: () => {}, readOnly: false };
